import React, { useEffect, useState } from "react";
import axios from "axios";
import { Flame, Plus, Trash2, CheckCircle2, Copy, Phone, MapPin } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_URL}/api` });

const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyLine = () => ({ brand: "", size: "", qty: 1 });

export default function PublicOrder() {
  const [catalog, setCatalog] = useState({ brands: [], sizes: [] });
  const [form, setForm] = useState({
    party_name: "", phone: "", email: "", address: "",
    date: todayISO(), notes: "",
    items: [emptyLine()],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/public/catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const updateLine = (i, patch) => setForm({
    ...form,
    items: form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
  });
  const addLine = () => setForm({ ...form, items: [...form.items, emptyLine()] });
  const removeLine = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const items = form.items
      .filter((it) => (it.qty || 0) > 0 && (it.brand || it.size))
      .map((it) => ({ brand: it.brand, size: it.size, qty: Number(it.qty) || 0 }));
    if (!form.party_name.trim()) { setError("Please enter your name"); return; }
    if (!form.phone.trim()) { setError("Please enter a phone number so we can reach you"); return; }
    if (items.length === 0) { setError("Please add at least one cylinder with a quantity"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/orders", { ...form, items });
      setDone(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Sorry, we couldn't submit your order. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F5F5F4] flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-sm border border-gray-200 p-8 shadow-sm text-center" data-testid="order-confirmation">
          <CheckCircle2 size={48} className="text-[#15803D] mx-auto" />
          <div className="mt-4 small-label text-[#15803D]">Order received</div>
          <h1 className="font-heading font-black text-3xl mt-1">Thank you!</h1>
          <div className="text-sm text-gray-600 mt-2">
            We&apos;ve received your order and our team will call you shortly to confirm the delivery.
          </div>
          <div className="mt-6 border border-dashed border-gray-300 rounded-sm p-4 bg-gray-50">
            <div className="small-label text-gray-500">Confirmation No.</div>
            <div className="data-num text-2xl font-black tracking-widest mt-1" data-testid="order-conf-no">
              {done.confirmation_no}
            </div>
            <button
              onClick={() => navigator.clipboard?.writeText(done.confirmation_no).catch(() => {})}
              className="mt-2 text-xs text-gray-500 hover:text-black inline-flex items-center gap-1">
              <Copy size={11} /> Copy
            </button>
          </div>
          <button
            onClick={() => { setDone(null); setForm({ party_name: "", phone: "", email: "", address: "", date: todayISO(), notes: "", items: [emptyLine()] }); }}
            className="mt-6 btn-primary h-10 px-5 text-sm"
            data-testid="place-another">
            Place another order
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F4] p-4" data-testid="public-order-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6 mt-4">
          <Flame size={26} className="text-[#B45309]" />
          <div>
            <div className="font-heading font-black text-2xl leading-tight">Place a Gas Order</div>
            <div className="text-xs text-gray-500">Fill this quick form — our team will call to confirm.</div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-sm p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="small-label block mb-1.5">Your name *</label>
              <input required value={form.party_name}
                onChange={(e) => setForm({ ...form, party_name: e.target.value })}
                className="input-lg" placeholder="Business or personal name"
                data-testid="po-name" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Phone *</label>
              <div className="relative">
                <Phone size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input required value={form.phone} type="tel"
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="input-lg pl-8" placeholder="10-digit mobile"
                  data-testid="po-phone" />
              </div>
            </div>
            <div>
              <label className="small-label block mb-1.5">Email (optional)</label>
              <input type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-lg" placeholder="for bill copy"
                data-testid="po-email" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Preferred delivery date</label>
              <input type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="input-lg"
                data-testid="po-date" />
            </div>
            <div className="sm:col-span-2">
              <label className="small-label block mb-1.5">Delivery address</label>
              <div className="relative">
                <MapPin size={13} className="absolute left-2.5 top-3 text-gray-400" />
                <textarea value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="input-lg pl-8 min-h-[70px] py-2" placeholder="Building, street, landmark, city"
                  data-testid="po-address" />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="small-label">Cylinders needed *</label>
              <button type="button" onClick={addLine}
                className="text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-black inline-flex items-center gap-1"
                data-testid="po-add-line">
                <Plus size={12} /> Add another
              </button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`po-line-${i}`}>
                  <div className="col-span-5">
                    {i === 0 && <div className="text-[10px] text-gray-500 mb-1">Brand</div>}
                    <select className="input-lg h-10 text-sm" value={it.brand}
                      onChange={(e) => updateLine(i, { brand: e.target.value })}
                      data-testid={`po-brand-${i}`}>
                      <option value="">Any brand</option>
                      {catalog.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="col-span-4">
                    {i === 0 && <div className="text-[10px] text-gray-500 mb-1">Size</div>}
                    <select className="input-lg h-10 text-sm" value={it.size}
                      onChange={(e) => updateLine(i, { size: e.target.value })}
                      data-testid={`po-size-${i}`}>
                      <option value="">Select size</option>
                      {catalog.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <div className="text-[10px] text-gray-500 mb-1">Qty</div>}
                    <input type="number" min="1" className="input-lg h-10 text-sm"
                      value={it.qty}
                      onChange={(e) => updateLine(i, { qty: e.target.value })}
                      data-testid={`po-qty-${i}`} />
                  </div>
                  <div className="col-span-1">
                    {form.items.length > 1 && (
                      <button type="button" onClick={() => removeLine(i)}
                        className="h-10 w-10 flex items-center justify-center text-gray-400 hover:text-[#B91C1C]"
                        data-testid={`po-remove-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="small-label block mb-1.5">Notes (optional)</label>
            <textarea value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-lg min-h-[70px] py-2"
              placeholder="Any special instructions, preferred time, gate code…"
              data-testid="po-notes" />
          </div>

          {error && (
            <div className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-sm px-3 py-2" data-testid="po-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting}
            className="btn-primary w-full h-12 text-sm font-black tracking-wider uppercase"
            data-testid="po-submit">
            {submitting ? "Sending…" : "Place order"}
          </button>
          <div className="text-[10px] text-gray-500 text-center">
            No login required. We&apos;ll call your phone to confirm the order.
          </div>
        </form>

        <div className="text-center mt-6 text-[11px] text-gray-500">
          Powered by Siddhi Star Gas · Ordering portal
        </div>
      </div>
    </div>
  );
}
